import { Button, Icon } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import TextAreaField from '@trustoo/components/input/TextAreaField'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { answerReclaim } from 'actions/reclaims'
import DatePicker from 'components/DatePicker'
import A from 'components/ui/A'
import { getDate } from 'date'
import format from 'date-fns/format'
import { useAppDispatch } from 'hooks/hooks'
import { ReclaimActionContext } from 'interfaces/Reclaims'
import { ReactNode, useState } from 'react'

// approve = accept within policy, approveAlt = accept outside policy, reject = deny.
export type AnswerActionId = 'approve' | 'approveAlt' | 'reject'

interface Props {
    reclaim: ReclaimActionContext
    answer: AnswerActionId
    onSuccess?: () => void
}

interface DetailRow {
    label: string
    value: ReactNode
}

/**
 * Modal for answering an open reclaim (accept, accept outside policy, or reject).
 * Keeps the optional reason, custom uncharge date and "accept outside policy" behaviour and
 * dispatches the existing answerReclaim thunk. Shown via NiceModal from ReclaimActionsMenu.
 */
const ReclaimAnswerModal = NiceModal.create(({ reclaim, answer, onSuccess }: Props) => {
    const isAccept = answer !== 'reject'
    const acceptAlt = answer === 'approveAlt'
    // A remark is only mandatory when rejecting or accepting outside policy; a normal accept is optional.
    const commentRequired = answer === 'reject' || answer === 'approveAlt'
    const modal = useModal()
    const dispatch = useAppDispatch()
    const isPendingAiProcessing = reclaim.is_pending_ai_processing
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [date, setDate] = useState<string | null>(null)
    const [rejectReason, setRejectReason] = useState(reclaim.admin_comment || '')

    const onClose = () => {
        if (loading) {
            return
        }
        modal.resolve()
        modal.hide()
    }

    const onReclaim = () => {
        setLoading(true)
        setError(null)
        dispatch(
            answerReclaim(reclaim.reclaim_id, isAccept ? 'accept' : 'reject', rejectReason, {
                date,
                alt: acceptAlt,
            }),
        ).then(
            () => {
                setLoading(false)
                onSuccess?.()
                modal.resolve()
                modal.hide()
            },
            ({ error: errorMessage }: { error?: string } = {}) => {
                setLoading(false)
                setError(errorMessage || 'Something went wrong')
            },
        )
    }

    const heading = (() => {
        if (acceptAlt) {
            return (
                <>
                    Accept reclaim <Icon name="offline_pin" orange />
                </>
            )
        }
        if (isAccept) {
            return (
                <>
                    Accept reclaim <Icon name="check" green />
                </>
            )
        }
        return (
            <>
                Reject reclaim <Icon name="cancel" red />
            </>
        )
    })()

    const requestUrl = `/service/requests/request/${reclaim.requestId}`
    const fullName = `${reclaim.firstName || ''} ${reclaim.lastName || ''}`.trim()
    const details: DetailRow[] = [
        { label: 'Reclaim', value: `#${reclaim.reclaim_id}` },
        { label: 'Name', value: reclaim.businessName },
        {
            label: 'Request date',
            value: reclaim.matchDate ? format(getDate(reclaim.matchDate), 'd-MMM-yyyy') : null,
        },
        { label: 'Reason', value: reclaim.reason },
        {
            label: 'For request',
            value: fullName ? (
                <A className="classic" to={requestUrl} external={false}>
                    {fullName}
                </A>
            ) : null,
        },
    ].filter((row) => row.value)

    return (
        <BaseModal onClose={onClose} maxWidth={520}>
            <BaseModalHeader>{heading}</BaseModalHeader>
            <BaseModalMainContent>
                <div className="flex flex-col gap-4">
                    {isPendingAiProcessing && (
                        <div className="flex items-start gap-2 rounded-lg border border-solid border-[#ffe08a] bg-[#fff9db] p-3 text-[13px] text-[#7a5b00]">
                            <Icon name="warning" orange fontSize={18} />
                            <span>
                                This reclaim is still pending AI processing. Are you sure you want
                                to handle it manually?
                            </span>
                        </div>
                    )}
                    {acceptAlt && (
                        <div className="flex items-start gap-2 rounded-lg border border-solid border-[#ffd8a8] bg-[#fff4e6] p-3 text-[13px] text-[#8a5300]">
                            <Icon name="offline_pin" orange fontSize={18} />
                            <span>
                                The provided reason falls outside the reclaim policy, but the
                                reclaim is granted anyway.
                            </span>
                        </div>
                    )}

                    <dl className="grid grid-cols-[7rem_1fr] gap-x-4 gap-y-2.5 rounded-lg border border-solid border-[#e9ecef] bg-white p-4">
                        {details.map(({ label, value }) => (
                            <div key={label} className="contents">
                                <dt className="text-[13px] text-[#6b7280]">{label}</dt>
                                <dd className="m-0 break-words text-[13px] font-medium text-[#1f2430]">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>

                    {reclaim.advanzaId && (
                        <a
                            className="flex items-center gap-2 rounded-lg border border-solid border-[#ffc9c9] bg-[#fff5f5] p-3 text-[13px] font-semibold text-[#c92a2a] no-underline hover:underline"
                            href={`https://leads.advanza.nl/Company/wrapper?company_id=${reclaim.advanzaId}`}>
                            <Icon name="warning" red fontSize={18} />
                            Note! this company is also present on Advanza.
                        </a>
                    )}

                    <TextAreaField
                        legend={commentRequired ? 'Reason' : 'Reason (optional)'}
                        autoFocus
                        value={rejectReason}
                        onChange={({ target }) => setRejectReason(target.value)}
                    />

                    <div>
                        {!date && (
                            <button
                                type="button"
                                onClick={() => setDate(format(new Date(), 'yyyy-MM-dd'))}
                                className="cursor-pointer border-none bg-transparent p-0 text-[13px] font-medium text-[#4c6ef5] hover:underline">
                                + Set custom date
                            </button>
                        )}
                        {date && (
                            <div className="flex flex-col gap-1.5">
                                <div className="flex items-center justify-between">
                                    <span className="text-[13px] text-[#6b7280]">
                                        Custom uncharge date
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setDate(null)}
                                        className="cursor-pointer border-none bg-transparent p-0 text-[12px] text-[#868e96] hover:underline">
                                        Clear
                                    </button>
                                </div>
                                <DatePicker
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    opened
                                />
                            </div>
                        )}
                    </div>

                    {error && <p className="m-0 text-[13px] text-[#c92a2a]">{error}</p>}
                </div>
            </BaseModalMainContent>
            <BaseModalFooter className="row justify-end gap-2">
                <Button appearance="secondary" onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    className="danger"
                    onClick={onReclaim}
                    disabled={loading || (commentRequired && rejectReason.trim().length < 1)}>
                    {loading ? <LoadingDots color="#dadada" /> : 'Confirm'}
                </Button>
            </BaseModalFooter>
        </BaseModal>
    )
})

export default ReclaimAnswerModal
